import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useToast from '../hooks/useToast';
import ToastContainer from './ToastContainer';
import api from '../services/api';
import { PAGE_TITLES, ROUTE_PATHS, SEARCH_ITEMS } from '../config/navigation';
import { ORGANIZATION_BRAND } from '../config/organization';
import { COMPANIES_UPDATED_EVENT } from '../utils/companyEvents';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, removeToast, showSuccess } = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(localStorage.getItem('activeCompanyId') || '');

  const syncSuperAdminCompanySelection = useCallback((list) => {
    const activeCompanies = list.filter((company) => company.isActive !== false);
    setCompanies(activeCompanies);

    const storedCompanyId = localStorage.getItem('activeCompanyId') || '';
    const hasStoredCompany =
      storedCompanyId && activeCompanies.some((company) => company._id === storedCompanyId);
    const selectedCompanyId = hasStoredCompany ? storedCompanyId : '';

    if (!selectedCompanyId) {
      localStorage.removeItem('activeCompanyId');
      localStorage.removeItem('activeCompanyName');
    } else {
      const selectedCompany = activeCompanies.find((company) => company._id === selectedCompanyId);
      localStorage.setItem('activeCompanyName', selectedCompany?.name || 'Seçili firma');
    }

    setActiveCompanyId(selectedCompanyId);
  }, []);

  const loadSuperAdminCompanies = useCallback(async () => {
    const companyResponse = await api.getCompanies();
    const list = companyResponse?.companies || [];
    syncSuperAdminCompanySelection(list);
  }, [syncSuperAdminCompanySelection]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await api.getProfile();

        if (response.success) {
          const profile = response.user;
          setUser(profile);
          localStorage.setItem('userData', JSON.stringify(profile));

          if (profile.role !== 'super_admin' && profile.companyId) {
            localStorage.setItem('activeCompanyId', profile.companyId);
            localStorage.setItem('activeCompanyName', profile.company || 'Kendi firmanız');
            setActiveCompanyId(profile.companyId);
          }

          if (profile.role === 'super_admin') {
            await loadSuperAdminCompanies();
          }
        }
      } catch (error) {
        console.error('Kullanıcı bilgileri alınamadı:', error);

        if (error.message.includes('401')) {
          localStorage.removeItem('userToken');
          localStorage.removeItem('isAuthenticated');
          navigate(ROUTE_PATHS.login);
        }
      } finally {
        setLoading(false);
      }
    };

    if (localStorage.getItem('userToken')) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [loadSuperAdminCompanies, navigate]);

  useEffect(() => {
    const handleCompaniesUpdated = () => {
      if (user?.role === 'super_admin') {
        loadSuperAdminCompanies().catch((error) => {
          console.error('Firma listesi yenilenemedi:', error);
        });
      }
    };

    window.addEventListener(COMPANIES_UPDATED_EVENT, handleCompaniesUpdated);
    return () => window.removeEventListener(COMPANIES_UPDATED_EVENT, handleCompaniesUpdated);
  }, [loadSuperAdminCompanies, user?.role]);

  const pageTitle = PAGE_TITLES[location.pathname] || ORGANIZATION_BRAND.name;

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const normalizedQuery = searchQuery.toLowerCase();

    return SEARCH_ITEMS.filter(
      (item) =>
        item.title.toLowerCase().includes(normalizedQuery) ||
        item.keywords.some((keyword) => keyword.toLowerCase().includes(normalizedQuery))
    );
  }, [searchQuery]);

  useEffect(() => {
    setSearchResults(filteredResults);
    setShowSearchResults(Boolean(searchQuery.trim()));
  }, [filteredResults, searchQuery]);

  const handleSearchInput = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultNavigation = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');

    if (result.external) {
      window.open(result.url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(result.url);
  };

  const handleSearch = (event) => {
    event.preventDefault();

    if (searchResults.length > 0) {
      handleResultNavigation(searchResults[0]);
    }
  };

  const toggleMobileSidebar = () => {
    const body = document.getElementsByTagName('body')[0];
    const overlay = document.querySelector('.overlay');
    body.classList.toggle('active');
    overlay.classList.toggle('active');
  };

  const handleCompanyChange = (event) => {
    const nextCompanyId = event.target.value;
    setActiveCompanyId(nextCompanyId);

    if (nextCompanyId) {
      const selectedCompany = companies.find((company) => company._id === nextCompanyId);
      localStorage.setItem('activeCompanyId', nextCompanyId);
      localStorage.setItem('activeCompanyName', selectedCompany?.name || 'Secili firma');
    } else {
      localStorage.removeItem('activeCompanyId');
      localStorage.removeItem('activeCompanyName');
    }

    window.location.reload();
  };

  const handleLogout = () => {
    if (!window.confirm('Çıkış yapmak istediğinizden emin misiniz?')) {
      return;
    }

    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('activeCompanyId');
    localStorage.removeItem('activeCompanyName');

    setUser(null);
    showSuccess('Güvenli çıkış yapıldı.');

    setTimeout(() => {
      navigate(ROUTE_PATHS.login);
    }, 1000);
  };

  return (
    <div className="main-header">
      <div className="d-flex">
        <div className="mobile-toggle" id="mobile-toggle" onClick={toggleMobileSidebar}>
          <i className="bx bx-menu"></i>
        </div>
        <div className="main-title">{pageTitle}</div>
      </div>

      <div className="d-flex align-items-center">
        {user?.role === 'super_admin' && (
          <div className="d-none d-md-flex align-items-center me-3" style={{ minWidth: '220px' }}>
            <select
              className="form-select form-select-sm"
              value={activeCompanyId}
              onChange={handleCompanyChange}
              aria-label="Aktif firma sec"
            >
              <option value="">Firma seçin</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <form className="app-search d-none d-lg-block" onSubmit={handleSearch}>
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Sayfalar, belgeler ve modüller içinde ara..."
              value={searchQuery}
              onChange={handleSearchInput}
              onFocus={() => searchQuery && setShowSearchResults(true)}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            />
            <span className="bx bx-search-alt" onClick={handleSearch}></span>

            {showSearchResults && (
              <div className="search-results-dropdown">
                {searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <div
                      key={`${result.url}-${index}`}
                      className="search-result-item"
                      onClick={() => handleResultNavigation(result)}
                    >
                      <i className="bx bx-file text-muted me-2"></i>
                      <div>
                        <div className="result-title">{result.title}</div>
                        <small className="text-muted">{result.type}</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="search-result-item no-results">
                    <i className="bx bx-search text-muted me-2"></i>
                    <div>
                      <div className="result-title">Sonuç bulunamadı</div>
                      <small className="text-muted">"{searchQuery}" için eşleşme yok</small>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        <div className="dropdown d-inline-block d-lg-none ms-2">
          <button
            type="button"
            className="btn header-item noti-icon waves-effect"
            id="page-header-search-dropdown"
            data-bs-toggle="dropdown"
          >
            <i className="bx bx-search-alt"></i>
          </button>
          <div className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
            <form className="p-3" onSubmit={handleSearch}>
              <div className="form-group m-0">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ara..."
                    value={searchQuery}
                    onChange={handleSearchInput}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-primary h-100" type="submit">
                      <i className="bx bx-search-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="dropdown d-inline-block">
          <button
            type="button"
            className="btn header-item noti-icon waves-effect"
            data-bs-toggle="dropdown"
            id="notificationDropdown"
          >
            <i className="bx bx-bell"></i>
            <span className="badge bg-danger rounded-pill">3</span>
          </button>
          <div className="dropdown-menu dropdown-menu-end" aria-labelledby="notificationDropdown">
            <div className="dropdown-header">
              <h6 className="mb-0">Bildirimler (3)</h6>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-info-circle text-primary me-3"></i>
              <div>
                <div className="fw-medium">Yeni proje ataması yapıldı</div>
                <small className="text-muted">2 saat önce</small>
              </div>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-message text-success me-3"></i>
              <div>
                <div className="fw-medium">İK ekibinden yeni mesaj var</div>
                <small className="text-muted">5 saat önce</small>
              </div>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-calendar text-warning me-3"></i>
              <div>
                <div className="fw-medium">Toplantı hatırlatması</div>
                <small className="text-muted">1 gün önce</small>
              </div>
            </div>
            <div className="dropdown-divider"></div>
            <div className="dropdown-item text-center text-primary">Tüm bildirimleri gör</div>
          </div>
        </div>

        <div className="dropdown d-inline-block">
          <button
            type="button"
            className="btn header-item waves-effect d-flex align-items-center"
            id="page-header-user-dropdown"
            data-bs-toggle="dropdown"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            ) : user ? (
              <>
                {user.avatar && user.avatar.filePath ? (
                  <img
                    src={`${apiUrl}/uploads/avatars/${user.avatar.filename}`}
                    alt="Profil"
                    className="rounded-circle header-profile-user me-2"
                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="rounded-circle header-profile-user me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#3C21F7',
                      fontSize: '12px'
                    }}
                  >
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </div>
                )}
                <span className="d-none d-md-inline-block">
                  <span className="fw-medium">
                    {user.firstName} {user.lastName}
                  </span>
                </span>
              </>
            ) : (
              <div
                className="rounded-circle header-profile-user me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#6c757d',
                  fontSize: '12px'
                }}
              >
                ?
              </div>
            )}
            <i className="bx bx-chevron-down ms-1"></i>
          </button>

          <div className="dropdown-menu dropdown-menu-end" aria-labelledby="page-header-user-dropdown">
            {user ? (
              <div className="dropdown-header">
                <h6 className="mb-0">
                  {user.firstName} {user.lastName}
                </h6>
                <small className="text-muted">{user.email}</small>
                <small className="text-muted d-block">
                  {user.department} - {user.position}
                </small>
              </div>
            ) : (
              <div className="dropdown-header">
                <h6 className="mb-0">Kullanıcı bilgisi yüklenemedi</h6>
                <small className="text-muted">Lütfen sayfayı yenileyin</small>
              </div>
            )}
            <div className="dropdown-divider"></div>
            <Link className="dropdown-item" to={ROUTE_PATHS.profile}>
              <i className="bx bx-user me-2"></i>
              <span>Profili Düzenle</span>
            </Link>
            <div className="dropdown-item">
              <i className="bx bx-cog me-2"></i>
              <span>Hesap Ayarları</span>
            </div>
            <div className="dropdown-item">
              <i className="bx bx-bell me-2"></i>
              <span>Bildirim Ayarları</span>
            </div>
            <div className="dropdown-item">
              <i className="bx bx-help-circle me-2"></i>
              <span>Yardım ve Destek</span>
            </div>
            <div className="dropdown-divider"></div>
            <button className="dropdown-item text-danger" onClick={handleLogout}>
              <i className="bx bx-power-off me-2 text-danger"></i>
              <span>Çıkış Yap</span>
            </button>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Header;
